import { Box, Typography } from "@mui/material"
import type { Risk_Trend } from "@phcglobal/connect-query"
import type React from "react"
import type { ReactNode } from "react"
import { extraColors } from "../../utils/theme"
import { TrendSVG } from "../LocationDetails/LocationRisk/TrendVisualIndicator"

export interface PillColors {
  background: string
  border: string
  text: string
}

export const pillVariantColors = {
  red: {
    background: extraColors.status.redDeep,
    border: extraColors.status.redLight,
    text: extraColors.status.redLight,
  },
  orange: {
    background: extraColors.status.orangeDeep,
    border: extraColors.status.orangeLight,
    text: extraColors.status.orangeLight,
  },
  yellow: {
    background: extraColors.status.yellowDeep,
    border: extraColors.status.yellowLight,
    text: extraColors.status.yellowLight,
  },
  green: {
    background: extraColors.status.greenDeep,
    border: extraColors.status.greenLight,
    text: extraColors.status.greenLight,
  },
  teal: {
    background: extraColors.status.navyMuted,
    border: extraColors.status.navyLight,
    text: extraColors.status.navyLight,
  },
  gray: {
    background: extraColors.medium,
    border: extraColors.medium,
    text: extraColors.dark,
  },
  white: {
    background: extraColors.disabled,
    border: extraColors.disabled,
    text: extraColors.dark,
  },
} satisfies Record<string, PillColors>

export type PillProps = {
  value: string | ReactNode
  trend?: Risk_Trend
} & (
  | { colors: PillColors; variant?: never }
  | { variant: keyof typeof pillVariantColors | undefined; colors?: never }
)

export const Pill: React.FC<PillProps> = ({
  colors: pillColors,
  value,
  trend,
  variant,
}) => {
  const colors = pillColors || (variant && pillVariantColors[variant])
  if (!colors) {
    return null
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: trend ? "space-between" : "center",
        alignItems: "center",
        gap: "5px",
        textTransform: "uppercase",
        backgroundColor: colors.background,
        borderRadius: "2px",
        padding: "1px 8px",
        borderColor: colors.border,
        borderWidth: "1px",
        borderStyle: "solid",
        maxHeight: 40,
        minWidth: "60px",
        width: "fit-content",
      }}
    >
      <Typography variant="tiny" color={colors.text}>
        {value}
      </Typography>
      {trend && <TrendSVG trend={trend} color={colors.border} size="small" />}
    </Box>
  )
}
